import React, { useCallback, useEffect, useRef } from 'react';
import TableHeader from '../../BaseComponents/TableHeader';
import './DoctorsTable.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
  appendDoctorsList,
  DoctorsPageState
} from '../../../store/doctorsSlice';
import DoctorsTableRow from './DoctorsTableRow';
import { getDoctorsList } from '../../../api/doctors';

const headerColumns = [
  { title: 'Avatar', key: 'avatar', minWidth: '64px', width: '64px', flex: 1 },
  { title: 'Doctor', key: 'doctor', minWidth: '180px', width: '180px', flex: 1 },
  { title: 'Calendly url', key: 'calendly', minWidth: '160px', width: '160px', flex: 1 },
  {
    title: 'Pre-scan consult Calendly',
    key: 'pre_scan_calendly',
    minWidth: '250px',
    width: '250px',
    flex: 1
  },
  {
    title: 'Specialization',
    key: 'specialization',
    minWidth: '160px',
    width: '160px',
    flex: 1
  },
  { title: 'Team', key: 'team', minWidth: '140px', width: '140px', flex: 1 },
  { title: 'Bio', key: 'bio', minWidth: '252px', width: '252px', flex: 1 },
  { title: 'Contacts', key: 'contacts', minWidth: '160px', width: '160px', flex: 1 },
  { title: 'Members', key: 'members', minWidth: '80px', width: '80px', flex: 1 },
  { title: '', key: 'actions', minWidth: '48px', width: '48px', flex: 1 }
];

const DoctorsTable: React.FC = () => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [hasMore, setHasMore] = React.useState<boolean>(true);
  const dispatch = useDispatch();
  const { doctorsList, limit } = useSelector(
    (state: { doctors: DoctorsPageState }) => state.doctors
  );
  const offsetRef = useRef<number>(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loadMoreDoctors = useCallback(async () => {
    if (loading || !hasMore) return;
    
    setLoading(true);
    offsetRef.current += limit;
    try {
      const doctorsList = await getDoctorsList(offsetRef.current, limit);
      if (doctorsList.doctors.length > 0) {
        dispatch(appendDoctorsList(doctorsList.doctors));
        setHasMore(doctorsList.doctors.length >= limit);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('fetchDoctors error :>> ', error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, limit, loading, hasMore]);

  const handleScroll = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (!scrollContainerRef.current || loading || !hasMore) return;
      
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const threshold = 100;
      
      if (scrollTop + clientHeight >= scrollHeight - threshold) {
        loadMoreDoctors();
      }
    }, 150);
  }, [loading, hasMore, loadMoreDoctors]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    }
    
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll]);

  const displayDoctors = doctorsList;
  return (
    <div className='scroll-container'>
      <div className='table-container doctors-table'>
        <TableHeader headerColumns={headerColumns} gap={'28px'} />
        <div className='table-body' ref={scrollContainerRef}>
          {displayDoctors?.map((doctor) => {
            return <DoctorsTableRow doctorData={doctor} key={doctor.id} />;
          })}
        </div>
      </div>
    </div>
  );
};

export default DoctorsTable;
